// Session store: the client-side state machine for one M6 game session.
//
// The store consumes validated inbound envelopes (wire.ts) and projects the
// authoritative session into renderable state. It never re-derives rules
// semantics: the exact state arrives from the C++ authority (via snapshot or
// accepted commandResult), and the client is forbidden from assembling even
// the genesis state itself — until the baseline snapshot lands, there is no
// board to show (baselinePending).
//
// Stream semantics mirrored from the M6 decision record:
//   - streamSeq is monotone per streamEpoch; holes in a resync backlog are
//     dropped (droppable) analysisUpdates, never authoritative loss, so a
//     forward jump is accepted while a repeat/rewind is ignored.
//   - a sessionBound with a different streamEpoch resets the incarnation
//     cursor: history is discarded and the baseline is re-awaited.
//   - REJECTED receipts occupy stream positions without advancing the
//     revision; accepted results chain revision tip+1. An accepted result
//     whose revision is neither tip+1 nor already covered is a protocol
//     violation: the store breaks (stops updating) rather than render an
//     unverifiable chain.
//   - a durable commit past the ACK deadline may advance the watermark
//     WITHOUT a published commandResult; the next snapshot (whose basis
//     covers the watermark) rebases the history — this is the designed
//     recovery path, not an anomaly.
//
// Fail-closed: any WireError from applyInbound breaks the store. A broken
// store ignores all further input; the UI offers a fresh reconnect.

import {
  WireError,
  validateSessionBound,
  validateSessionEventPayload,
  validateTransportErrorPayload,
  type AnalysisErrorPayload,
  type AnalysisUpdatePayload,
  type CommandErrorBody,
  type CommandResultPayload,
  type ExactState,
  type EventGroupSummary,
  type EventType,
  type ExportLogPayload,
  type InboundEnvelope,
  type LegalMaskPayload,
  type PongPayload,
  type SessionBoundPayload,
  type SessionErrorPayload,
  type SessionStatePayload,
  type SnapshotPayload,
} from "./wire.js";
import type { RestrictedValue } from "./restricted-json.js";

export type ConnectionPhase =
  | "idle"        // never connected
  | "connecting"  // socket open / bind in flight
  | "syncing"     // bound; backlog + baseline arriving
  | "live"        // server session is live
  | "recovering"  // server announced engine recovery
  | "faulted"     // sessionError received (session-scoped terminal)
  | "closed";     // socket closed; reconnect possible

export interface HistoryEntry {
  revision: number;
  semanticEventCursor: number;
  state: ExactState;
  exactStateSha256: string | null; // carried by commandResult; null on a snapshot baseline
  eventGroup: EventGroupSummary | null; // null on the baseline entry
  commandId: string | null;
  redelivered: boolean;
}

export type PendingStatus = "inflight" | "unknown";
export interface PendingCommand {
  commandId: string;
  canonicalJson: string;
  status: PendingStatus;
}

export interface CommandReceipt {
  commandId: string;
  accepted: boolean;
  redelivered: boolean;
  error: CommandErrorBody | null;
  // Transport-domain rejection (never journaled) when set.
  transportErrorClass: string | null;
}

export class SessionStore {
  readonly sessionId: string;

  phase: ConnectionPhase = "idle";
  // Set when a wire violation or an unverifiable chain was seen; the store
  // is dead from that point (applyInbound is a no-op).
  broken: string | null = null;

  streamEpoch: string | null = null;
  lastSeq = 0;
  serverRevision = 0; // advertised by sessionBound
  created = false;
  baselinePending = true;

  history: HistoryEntry[] = [];
  cursor = 0; // replay cursor into history; follows the tip unless moved

  legalMask: LegalMaskPayload | null = null;
  analysis: AnalysisUpdatePayload | null = null;
  analysisError: AnalysisErrorPayload | null = null;
  sessionError: SessionErrorPayload | null = null;
  lastTransportError: { requestId: string | null; errorClass: string } | null = null;
  lastExport: ExportLogPayload | null = null;
  lastPong: PongPayload | null = null;

  readonly pending = new Map<string, PendingCommand>();
  readonly receipts = new Map<string, CommandReceipt>();
  // SGF import replay progress (display only; the receipts remain the
  // authoritative per-command outcome).
  importStatus: string | null = null;

  // Monotonic change counter for useSyncExternalStore-style snapshots: the
  // store mutates in place, so identity alone cannot signal a change.
  changeCounter = 0;

  #listeners = new Set<() => void>();

  constructor(sessionId: string) {
    this.sessionId = sessionId;
  }

  // Change subscription for the client/UI (one notification per applied
  // unit of work; listener exceptions are the caller's bug and propagate
  // to the caller, never into the store).
  subscribe(listener: () => void): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  #emit(): void {
    this.changeCounter++;
    for (const listener of this.#listeners) listener();
  }

  get tip(): HistoryEntry | null {
    return this.history.length === 0 ? null : this.history[this.history.length - 1]!;
  }

  get view(): HistoryEntry | null {
    return this.history.length === 0 ? null : this.history[this.cursor]!;
  }

  get isReplay(): boolean {
    return this.history.length > 0 && this.cursor < this.history.length - 1;
  }

  setCursor(index: number): void {
    if (this.history.length === 0) return;
    if (!Number.isSafeInteger(index)) return; // replay cursor is an array index
    this.cursor = Math.min(Math.max(index, 0), this.history.length - 1);
    this.#emit();
  }

  cursorLatest(): void {
    if (this.history.length > 0) this.cursor = this.history.length - 1;
    this.#emit();
  }

  setImportStatus(status: string | null): void {
    this.importStatus = status;
    this.#emit();
  }

  markConnecting(): void {
    if (this.broken !== null) return;
    this.phase = "connecting";
    this.#emit();
  }

  // Transport-layer violation observed by the client (e.g. a non-text
  // frame): same fail-closed semantics as a WireError from applyInbound.
  breakProtocol(reason: string): void {
    if (this.broken !== null) return;
    this.broken = reason;
    this.#emit();
  }

  // Socket closed (any reason). In-flight commands are now of unknown fate
  // (they may have committed without a receipt): they move to "unknown" and
  // the client resubmits them idempotently after the next baseline.
  markClosed(): void {
    if (this.broken !== null) return;
    for (const entry of this.pending.values())
      if (entry.status === "inflight") entry.status = "unknown";
    if (this.phase !== "faulted") this.phase = "closed";
    this.#emit();
  }

  // Transport-domain rejection of a submitCommand request: the command was
  // never journaled... with one exception — a DEADLINE verdict may race a
  // durable commit (the fsync crossed the ACK deadline). Only a non-DEADLINE
  // rejection clears the pending entry outright.
  commandTransportRejected(commandId: string, errorClass: string): void {
    if (this.broken !== null) return;
    const entry = this.pending.get(commandId);
    if (entry === undefined) return;
    if (errorClass === "DEADLINE") {
      entry.status = "unknown";
    } else {
      this.pending.delete(commandId);
      this.receipts.set(commandId, {
        commandId,
        accepted: false,
        redelivered: false,
        error: null,
        transportErrorClass: errorClass,
      });
    }
    this.#emit();
  }

  addPending(commandId: string, canonicalJson: string): void {
    this.pending.set(commandId, { commandId, canonicalJson, status: "inflight" });
    this.#emit();
  }

  applyInbound(envelope: InboundEnvelope): void {
    if (this.broken !== null) return;
    try {
      if (envelope.kind === "connectionEvent") this.#applyConnection(envelope.event);
      else this.#applySession(envelope.event);
    } catch (error) {
      if (error instanceof WireError) {
        this.broken = error.message;
        this.#emit();
        return;
      }
      throw error;
    }
    this.#emit();
  }

  #applyConnection(event: {
    sessionId: string | null;
    requestId: string | null;
    type: "sessionBound" | "transportError";
    payload: unknown;
  }): void {
    if (event.type === "transportError") {
      const { errorClass } = validateTransportErrorPayload(event.payload as RestrictedValue);
      this.lastTransportError = { requestId: event.requestId, errorClass };
      return;
    }
    const bound: SessionBoundPayload = validateSessionBound(
      event.payload as RestrictedValue,
    );
    if (event.sessionId !== this.sessionId)
      throw new WireError("sessionBound carries a foreign sessionId");
    const epochChanged = bound.streamEpoch !== this.streamEpoch;
    if (epochChanged) {
      // Cross-epoch reset: the current incarnation's ring is served from its
      // start and a fresh snapshot baseline always follows a cursor-less
      // bind. Discard every projection of the dead epoch.
      this.history = [];
      this.cursor = 0;
      this.lastSeq = 0;
      this.baselinePending = true;
      this.legalMask = null;
      this.analysis = null;
      this.analysisError = null;
      this.receipts.clear();
      for (const entry of this.pending.values()) entry.status = "unknown";
    }
    const tipAtBind = this.tip;
    if (!epochChanged && tipAtBind !== null && bound.revision < tipAtBind.revision)
      // The per-session watermark never regresses within an epoch: a bound
      // behind the local tip is a protocol violation, not a usable baseline.
      throw new WireError("sessionBound advertises a revision behind the local tip");
    this.streamEpoch = bound.streamEpoch;
    this.serverRevision = bound.revision;
    // created is sticky-true: only this client's createSession can set it.
    // A same-epoch rebind (or an idempotent createSession retry after a lost
    // ACK) answers created:false, which must not erase the fresh-session
    // proof the import gate relies on.
    if (bound.created) this.created = true;
    if (this.phase !== "faulted") {
      // A same-epoch rebind with a live baseline AT the bound revision is
      // served the tail alone — no sessionState follows — so the store must
      // not park in "syncing". If the bound is AHEAD of the tip (a commit
      // landed while the client was away and published nothing), the tail
      // alone cannot close the gap: stay syncing until a snapshot does.
      this.phase =
        !epochChanged && !this.baselinePending && tipAtBind !== null &&
        tipAtBind.revision >= bound.revision
          ? "live"
          : "syncing";
    }
  }

  #applySession(event: { sessionId: string; streamSeq: number; type: string; payload: unknown }): void {
    if (event.sessionId !== this.sessionId)
      throw new WireError("sessionEvent carries a foreign sessionId");
    // Monotone cursor: a repeat/rewind is a duplicate delivery (ignored); a
    // forward jump is a dropped droppable analysisUpdate (accepted). The
    // baseline snapshot that always follows a cursor-less bind covers any
    // authoritative content behind the cursor.
    if (event.streamSeq <= this.lastSeq) return;
    this.lastSeq = event.streamSeq;

    const validated = validateSessionEventPayload(
      event.type as EventType,
      event.payload as RestrictedValue,
    );
    switch (validated.type) {
      case "sessionState":
        this.#applySessionState(validated.payload);
        return;
      case "sessionError":
        this.sessionError = validated.payload;
        this.phase = "faulted";
        return;
      case "commandResult":
        this.#applyCommandResult(validated.payload);
        return;
      case "snapshot":
        this.#applySnapshot(validated.payload);
        return;
      case "legalMaskRead": {
        // Basis-bound install: the mask is a read model AT its basis; an
        // answer that does not name the current tip exactly is stale and is
        // dropped, never displayed (mask staleness review).
        const tip = this.tip;
        if (
          tip !== null &&
          validated.payload.revision === tip.revision &&
          validated.payload.semanticEventCursor === tip.semanticEventCursor &&
          (tip.exactStateSha256 === null ||
            validated.payload.exactStateSha256 === tip.exactStateSha256)
        )
          this.legalMask = validated.payload;
        return;
      }
      case "exportLog":
        this.lastExport = validated.payload;
        return;
      case "pong":
        this.lastPong = validated.payload;
        return;
      case "analysisUpdate": {
        // Droppable: install only an analysis computed AT the exact tip — a
        // stale basis misrenders, and an ahead-of-tip basis describes a state
        // the client does not hold (pipelined ahead of the authoritative
        // cursor). Anything else is dropped, never displayed.
        const tip = this.tip;
        if (
          tip !== null &&
          validated.payload.basisRevision === tip.revision &&
          validated.payload.basisSemanticEventCursor === tip.semanticEventCursor
        )
          this.analysis = validated.payload;
        return;
      }
      case "analysisError":
        this.analysisError = validated.payload;
        return;
    }
  }

  #applySessionState(payload: SessionStatePayload): void {
    if (payload.state === "recovering") {
      if (this.phase !== "faulted") this.phase = "recovering";
      return;
    }
    // live: liveness is declared only AT the exact local tip. A live notice
    // ahead of the tip describes a commit the client has not covered (stay
    // syncing; the backlog or a snapshot closes the gap), one behind the tip
    // is a watermark regression (fail closed), and without a baseline there
    // is nothing to render (the baseline snapshot flips live instead).
    if (this.phase === "faulted") return;
    const revision = payload.revision!;
    const tip = this.tip;
    if (this.baselinePending || tip === null) return;
    if (revision < tip.revision)
      throw new WireError(
        `sessionState(live) revision ${revision} trails the local tip ${tip.revision}`,
      );
    if (revision > tip.revision || revision < this.serverRevision) {
      // Ahead of the tip (a silent commit is outstanding) or trailing the
      // bound watermark (a stale tail entry replayed during resync): not
      // liveness. Mark syncing so the client's re-cover pull is unambiguous.
      if (this.phase !== "syncing") this.phase = "syncing";
      return;
    }
    this.phase = "live";
  }

  // Liveness convergence: a syncing store whose tip reaches the bound
  // watermark is live — the tail/snapshot has closed the gap a sessionBound
  // or a silent (DEADLINE) commit opened.
  #maybeEnterLive(): void {
    if (this.phase === "faulted" || this.baselinePending) return;
    const tip = this.tip;
    if (tip !== null && tip.revision >= this.serverRevision && this.phase !== "live")
      this.phase = "live";
  }

  #applyCommandResult(payload: CommandResultPayload): void {
    const { commandId, result, redelivered } = payload;
    this.pending.delete(commandId);
    this.receipts.set(commandId, {
      commandId,
      accepted: result.accepted,
      redelivered,
      error: result.error,
      transportErrorClass: null,
    });
    if (!result.accepted) return; // a receipt occupies a stream position only
    const nextState = result.nextState!;
    const tip = this.tip;
    if (tip === null) {
      // Pre-baseline traffic on a cursor-less bind: the baseline snapshot
      // (basis ≥ every backlog revision) arrives last and covers this.
      return;
    }
    if (nextState.revision <= tip.revision) return; // already covered (redelivery)
    if (nextState.revision !== tip.revision + 1)
      throw new WireError(
        `accepted commandResult revision ${nextState.revision} does not chain from tip ${tip.revision}`,
      );
    // The event group must commit from exactly the tip position.
    const group = result.eventGroup!;
    if (group.basisRevision !== tip.revision ||
        group.basisSemanticEventCursor !== tip.semanticEventCursor)
      throw new WireError("eventGroup basis does not match the tip position");
    // The basis moved: drop basis-bound read models (legal mask; an analysis
    // of an older revision).
    this.legalMask = null;
    if (this.analysis !== null && this.analysis.basisRevision < nextState.revision)
      this.analysis = null;
    // The cursor follows the tip only when it was AT the tip; a user
    // replaying an earlier revision keeps their position.
    const wasAtTip = this.cursor === this.history.length - 1;
    this.history.push({
      revision: nextState.revision,
      semanticEventCursor: nextState.semanticEventCursor,
      state: nextState,
      exactStateSha256: result.exactStateSha256,
      eventGroup: result.eventGroup,
      commandId,
      redelivered,
    });
    if (wasAtTip) this.cursor = this.history.length - 1;
    this.#maybeEnterLive();
  }

  #applySnapshot(payload: SnapshotPayload): void {
    const tip = this.tip;
    if (this.baselinePending) {
      // Cursor-less binds replay the historical ring before a fresh
      // snapshot. Old read models are valid traffic, but cannot establish
      // the baseline: stay syncing until the bound watermark is covered.
      if (payload.state.revision < this.serverRevision) return;
    } else if (tip !== null) {
      if (payload.state.revision < tip.revision) return; // stale read-model answer
      if (payload.state.revision === tip.revision) {
        // Refresh of the current tip: the digest must agree with what the
        // commandResult carried (when it did); disagreement breaks the store.
        if (
          tip.exactStateSha256 !== null &&
          tip.exactStateSha256 !== payload.exactStateSha256
        )
          throw new WireError("snapshot digest disagrees with the applied commandResult");
        this.baselinePending = false;
        return;
      }
      // revision > tip.revision: a durable commit crossed the ACK deadline
      // and published no commandResult — the snapshot covers it. Rebase.
    }
    this.history = [{
      revision: payload.state.revision,
      semanticEventCursor: payload.state.semanticEventCursor,
      state: payload.state,
      exactStateSha256: payload.exactStateSha256,
      eventGroup: null,
      commandId: null,
      redelivered: false,
    }];
    this.cursor = 0;
    this.baselinePending = false;
    this.legalMask = null;
    this.analysis = null;
    this.analysisError = null;
    this.#maybeEnterLive();
  }
}
